import {Component, Mixins, Watch} from 'vue-property-decorator';
import debounce from 'lodash.debounce';
import PredictiveInput from '@/components/PredictiveInput/PredictiveInput.vue';
import {searchDvdOrders} from '@/dvd-orders/DvdOrderService';
import {PublicDvdSearchResults, SportEventDvd} from '@/dvd-orders/DvdModel';
import LoadingMixin from '@/mixins/LoadingMixin';
import {DateTime} from 'luxon';
import CancelMixin from '@/dvd-orders/mixins/CancelMixin';
import {Page} from '@/router';
import { DisplayOption } from '@/components/interfaces/ComponentOptions';

@Component({
    components: {
        PredictiveInput
    },
    filters: {
        dvdText(inCart: boolean): string {
            return inCart ? 'Added to Cart' : '+ Add to Cart';
        }
    }
})
export default class DvdOrder extends Mixins(LoadingMixin, CancelMixin) {
    private year = 0;
    private years: number[] = [];
    private results: PublicDvdSearchResults = { foundCount: 0, dvds: [] };
    private searchText = '';
    private pageSize = 50;
    private pageNumber = 1;
    private searchDropdownOptions: DisplayOption[] = [];

    @Watch('year')
    @Watch('searchText')
    searchTextChanged() {
        this.debounceSearch();
    }

    get filterSelected(): boolean {
        return Boolean(this.year || this.searchText);
    }

    get inCart(): number {
        return this.$store.state.dvdOrderDetails.items.length;
    }

    get pageCount(): number {
        return Math.ceil(this.results.foundCount / this.pageSize);
    }

    public created() {
        const now = new Date();
        while (now.getFullYear() >= 2006) {
            this.years.push(now.getFullYear());
            now.setFullYear(now.getFullYear() - 1);
        }

        this.debounceSearch();
        this.setRecentSearch();
    }

    get debounceRecentSearchToCache(): () => void {
        return debounce(this.setRecentSearch, 1500, { maxWait: 3000 });
    }

    setRecentSearch() {
        if (this.searchText) {
            this.$store.commit('addSearch', this.searchText);
        }

        const recentSearches = this.$store.getters.recentSearches;
        if (recentSearches && recentSearches.length) {
            this.searchDropdownOptions = recentSearches.map((name: string) => ({id: 0, name}));
        }
    }

    async clearFilters() {
        this.year = 0;
        this.searchText = '';
        this.debounceSearch();
    }

    /*
     * DON'T CALL THIS METHOD DIRECTLY. Call this.debounceSearch() instead.
     * This guarantees that even if the user is typing it only gets called when they're done
     */
    async search(page = 1) {
        await this.execute(async () => {
            const results = await searchDvdOrders(this.searchText, this.year, page, this.pageSize);
            results.dvds.forEach(dvd => {
                dvd.inCart = this.$store.getters.isInCart(dvd);
                dvd.formattedDate = DateTime.fromISO(dvd.date, { zone: 'America/Chicago'}).toLocaleString(DateTime.DATE_FULL);
            });
            this.results = results;
            if (this.pageNumber !== page) {
                this.pageNumber = page;
            }
        } , "We're sorry, an error occurred while loading DVDs. Please try again. If the issue persists, contact us");

        this.debounceRecentSearchToCache();
    }

    addDvd(dvd: SportEventDvd) {
        this.$store.commit('addToCart', dvd);
        dvd.inCart = true;
    }

    removeDvd(dvd: SportEventDvd) {
        this.$store.commit('removeFromCart', dvd);
        dvd.inCart = false;
    }

    overridenClearCart() {
        this.clearCart();
        this.results.dvds.forEach(dvd => dvd.inCart = false);
    }

    gotoCart() {
        if (this.inCart > 0) {
            this.$router.push({ name: Page.CART }).catch(() => {});
        }
    }

    handlePagination(page: number) {
        this.search(page);
    }

    /*
     * Because we want to search as the user types we "debounce" the search.
     * It only fires when the user hasn't typed for at least 300 seconds, but fires at least once every second.
     */
    get debounceSearch(): () => void {
        return debounce(this.search, 300, { maxWait: 1000 });
    }
}
